import { useState, useRef, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { bytesToKb } from '../../../@iosper/utils/Format';

const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1024,
  useWebWorker: true,
};

export const useFileUploadBox = ({
  files = [],
  onFilesChange,
  maxFiles = 3,
  disabled = false,
  compressFiles = true,
  setMessage,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(null);

  const isMaxFiles = files.length >= maxFiles;

  const isDuplicateFile = useCallback(
    (newFile) =>
      files.some(
        (f) =>
          f.name === newFile.name &&
          f.size === newFile.size &&
          f.lastModified === newFile.lastModified,
      ),
    [files],
  );

  const compressImageFile = useCallback(async (file) => {
    if (bytesToKb(file.size) <= 600) return file;
    try {
      const blob = await imageCompression(file, {
        ...COMPRESSION_OPTIONS,
        onProgress: setCompressionProgress,
      });
      return new File([blob], file.name, {
        type: blob.type || file.type,
        lastModified: file.lastModified || Date.now(),
      });
    } catch {
      return file;
    }
  }, []);

  const handleFileSelect = useCallback(
    async (fileList) => {
      if (disabled || isMaxFiles) return;

      const fileArray = Array.from(fileList);
      const remainingSlots = maxFiles - files.length;

      for (const file of fileArray) {
        if (isDuplicateFile(file)) {
          setMessage({
            open: true,
            message: `El archivo "${file.name}" ya fue agregado`,
            severity: 'error',
          });
          return;
        }
      }

      if (fileArray.length > remainingSlots) {
        setMessage({
          open: true,
          message: `Solo puede agregar ${remainingSlots} archivo(s) más. Máximo: ${maxFiles}`,
          severity: 'error',
        });
        return;
      }

      setIsCompressing(true);
      setCompressionProgress(0);

      try {
        const processed = [];
        for (let i = 0; i < fileArray.length; i++) {
          const file = fileArray[i];
          const out =
            compressFiles && file.type?.startsWith('image/')
              ? await compressImageFile(file)
              : file;
          processed.push(out);
          setCompressionProgress(((i + 1) / fileArray.length) * 100);
        }
        onFilesChange([...files, ...processed]);
      } catch {
        setMessage({ open: true, message: 'Error al procesar los archivos', severity: 'error' });
      } finally {
        setIsCompressing(false);
        setCompressionProgress(null);
      }
    },
    [disabled, isMaxFiles, maxFiles, files, isDuplicateFile, compressFiles, compressImageFile, onFilesChange, setMessage],
  );

  const handleButtonClick = useCallback(() => {
    if (!disabled && !isMaxFiles && fileInputRef.current) fileInputRef.current.click();
  }, [disabled, isMaxFiles]);

  const handleInputChange = useCallback(
    (e) => {
      if (e.target.files?.length) handleFileSelect(e.target.files);
      e.target.value = '';
    },
    [handleFileSelect],
  );

  const handleRemoveFile = useCallback(
    (index) => {
      if (!disabled) onFilesChange(files.filter((_, i) => i !== index));
    },
    [disabled, files, onFilesChange],
  );

  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isMaxFiles) setIsDragging(true);
    },
    [disabled, isMaxFiles],
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!disabled && e.dataTransfer.files?.length) handleFileSelect(e.dataTransfer.files);
    },
    [disabled, handleFileSelect],
  );

  const getFileIconType = useCallback((file) => {
    if (file.type === 'application/pdf') return 'pdf';
    if (file.type?.startsWith('image/')) return 'image';
    return 'file';
  }, []);

  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }, []);

  const getTotalSizeMB = useCallback(
    () => files.reduce((sum, f) => sum + (f.size || 0), 0) / (1024 * 1024),
    [files],
  );

  return {
    fileInputRef,
    isDragging,
    isCompressing,
    compressionProgress,
    isMaxFiles,
    getFileIconType,
    formatFileSize,
    getTotalSizeMB,
    handleButtonClick,
    handleInputChange,
    handleRemoveFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  };
};
